import { useState } from 'react';
import { HandCoins, Search } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useAccountsQuery, useCreatePaymentMutation, usePaymentMethodsQuery, useSalesQuery,
  type Sale,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDate, formatMoney } from '@/lib/format';
import {
  Alert, Badge, Button, Card, EmptyState, ErrorState, Input, LoadingState, Modal,
  PageHeader, Pagination, Select, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { SummaryCards } from '@/components/SummaryCards';
import { ALL_TIME, DateRangeFilter, type DateRange } from '@/components/DateRangeFilter';
import { SaleDetailModal } from './SaleDetailModal';

/** Days since a sale was issued — what makes a debt feel old. */
const ageOf = (issuedAt: string): number =>
  Math.max(0, Math.floor((Date.now() - new Date(issuedAt).getTime()) / 86_400_000));

const AGE_TONE = (days: number): 'neutral' | 'warning' | 'danger' =>
  days >= 60 ? 'danger' : days >= 30 ? 'warning' : 'neutral';

/**
 * What customers still owe, and taking money against it.
 *
 * A payment here is allocated to the sale it is paid against, so a customer who
 * hands over part of what they owe leaves the rest outstanding on that sale
 * rather than as a credit floating on their account. Part payments are the
 * normal case on credit, not an exception.
 */
export function ReceivablesPage() {
  const format = useFormat();

  const [page, setPage] = useState(1);
  const [range, setRange] = useState<DateRange>(ALL_TIME);
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [paying, setPaying] = useState<Sale | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState({ amount: '', methodId: '', accountId: '', reference: '' });

  const { data, isLoading, isError, error, refetch, isFetching } = useSalesQuery({
    page,
    limit: 25,
    outstandingOnly: true,
    search: search || undefined,
    from: range.from || undefined,
    to: range.to || undefined,
  });

  const { data: methods } = usePaymentMethodsQuery();
  const { data: accounts } = useAccountsQuery({ limit: 50 });
  const [createPayment, paymentState] = useCreatePaymentMutation();

  const method = methods?.find((entry) => entry.id === form.methodId);
  const outstanding = paying ? Number(paying.dueAmount) : 0;
  const amount = Number(form.amount);
  const overpaying = amount > outstanding;

  const open = (sale: Sale): void => {
    setFailure(null);
    setNotice(null);
    setPaying(sale);
    // The whole balance is the common case; anything less is typed over it.
    setForm({
      amount: sale.dueAmount,
      methodId: methods?.find((entry) => entry.isDefault)?.id ?? methods?.[0]?.id ?? '',
      accountId: '',
      reference: '',
    });
  };

  const submit = async (): Promise<void> => {
    if (!paying) return;
    setFailure(null);
    try {
      await createPayment({
        methodId: form.methodId,
        amount,
        customerId: paying.customerId ?? undefined,
        accountId: form.accountId || undefined,
        reference: form.reference || undefined,
        allocations: [{ saleId: paying.id, amount: Math.min(amount, outstanding) }],
      }).unwrap();

      setNotice(
        amount < outstanding
          ? `Part payment taken. ${formatMoney((outstanding - amount).toFixed(2), format)} still outstanding on ${paying.number}.`
          : `${paying.number} settled.`,
      );
      setPaying(null);
    } catch (paymentError) {
      setFailure(apiErrorMessage(paymentError, 'That payment could not be recorded'));
    }
  };

  return (
    <>
      <PageHeader
        title="Receivables"
        description="Sales that still owe money, oldest debts first."
      />

      {failure && <div className="mb-4"><Alert tone="danger">{failure}</Alert></div>}
      {notice && <div className="mb-4"><Alert tone="success">{notice}</Alert></div>}

      {data?.summary && (
        <SummaryCards
          stats={[
            { label: 'Sales owing', value: String(data.summary.count) },
            {
              label: 'Outstanding',
              value: formatMoney(String(data.summary.outstanding), format),
              hint: 'what customers owe',
              tone: 'owed',
            },
            { label: 'Collected', value: formatMoney(String(data.summary.collected), format) },
            {
              label: 'Sold',
              value: formatMoney(String(data.summary.total), format),
              hint: 'value of these sales',
            },
          ]}
        />
      )}

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-subtle p-4">
          <div className="min-w-56 flex-1">
            <Input
              label="Search"
              icon={Search}
              placeholder="Number or customer"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <DateRangeFilter
            value={range}
            onChange={(next) => {
              setRange(next);
              setPage(1);
            }}
          />
        </div>

        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={HandCoins}
            title={search ? 'Nothing matches that search' : 'Nothing is owed'}
            description={
              search ? 'Try a different name or number.' : 'Every confirmed sale has been paid.'
            }
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Sale</Th>
                  <Th>Customer</Th>
                  <Th>Issued</Th>
                  <Th numeric>Total</Th>
                  <Th numeric>Paid</Th>
                  <Th numeric>Outstanding</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className={isFetching ? 'opacity-60' : undefined}>
                {data?.items.map((sale) => {
                  const days = ageOf(sale.issuedAt);
                  return (
                    <tr key={sale.id} className="hover:bg-sunken">
                      <Td>
                        <button
                          type="button"
                          className="font-medium hover:underline"
                          onClick={() => setOpenId(sale.id)}
                        >
                          {sale.number}
                        </button>
                      </Td>
                      <Td className="text-secondary">{sale.customer?.name ?? 'Walk-in'}</Td>
                      <Td>
                        <span className="text-secondary">{formatDate(sale.issuedAt, format)}</span>
                        <Badge tone={AGE_TONE(days)} className="ml-2">{days}d</Badge>
                      </Td>
                      <Td numeric className="text-secondary">{formatMoney(sale.total, format)}</Td>
                      <Td numeric className="text-secondary">{formatMoney(sale.paidAmount, format)}</Td>
                      <Td numeric className="font-medium">{formatMoney(sale.dueAmount, format)}</Td>
                      <Td className="text-right">
                        <Can permission={P.PAYMENT_CREATE}>
                          <Button size="sm" variant="outline" onClick={() => open(sale)}>
                            Take payment
                          </Button>
                        </Can>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            {data && (
              <Pagination
                page={data.meta.page}
                totalPages={data.meta.totalPages}
                total={data.meta.total}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      {paying && (
        <Modal
          open
          onClose={() => setPaying(null)}
          title={`Payment against ${paying.number}`}
          description={`${paying.customer?.name ?? 'Walk-in'} owes ${formatMoney(paying.dueAmount, format)}.`}
          footer={
            <>
              <Button variant="outline" onClick={() => setPaying(null)}>Cancel</Button>
              <Button
                loading={paymentState.isLoading}
                disabled={!form.methodId || !(amount > 0)}
                onClick={() => void submit()}
              >
                Record payment
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <Input
              label="Amount"
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(event) => setForm({ ...form, amount: event.target.value })}
              hint="Part of what is owed is fine — the rest stays on the sale."
            />

            {overpaying && (
              <Alert tone="warning">
                That is more than {paying.number} owes. The extra stays on the customer&rsquo;s
                account as unallocated credit rather than being refunded here.
              </Alert>
            )}

            <Select
              label="Method"
              required
              value={form.methodId}
              onChange={(event) => setForm({ ...form, methodId: event.target.value })}
            >
              <option value="">Select a method</option>
              {methods?.map((entry) => (
                <option key={entry.id} value={entry.id}>{entry.name}</option>
              ))}
            </Select>

            <Select
              label="Into account"
              value={form.accountId}
              onChange={(event) => setForm({ ...form, accountId: event.target.value })}
              hint={
                method?.account
                  ? `Leave blank for ${method.account.name}, where ${method.name} normally lands.`
                  : 'Where the money lands, so the day can be balanced.'
              }
            >
              <option value="">{method?.account?.name ?? 'Select an account'}</option>
              {accounts?.items
                .filter((account) => account.isActive)
                .map((account) => (
                  <option key={account.id} value={account.id}>{account.name}</option>
                ))}
            </Select>

            {method?.requiresReference && (
              <Input
                label="Reference"
                required
                value={form.reference}
                onChange={(event) => setForm({ ...form, reference: event.target.value })}
                hint={`${method.name} payments are traced by their reference.`}
              />
            )}
          </div>
        </Modal>
      )}

      {openId && <SaleDetailModal saleId={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}
